import React from "react";
import { useLanguage } from "@/lib/LanguageContext";
import { translateUiPhrase, uiPhraseTranslations } from "@/lib/uiPhraseTranslations";

const ATTRS = ["placeholder", "aria-label", "title", "alt"];
const TEXT_SELECTOR = "button,a,p,span,h1,h2,h3,h4,h5,h6,label,small,div,li,dt,dd,th,td";

function translateDynamic(value, lang) {
  if (!value || lang === "en") return value;
  const pins = value.match(/^(\d+)\s+pins$/i);
  if (pins) return `${pins[1]} ${translateUiPhrase("pins", lang)}`;
  return translateUiPhrase(value, lang);
}

function localizeElement(el, lang) {
  if (!el || el.closest?.("[data-no-auto-localize], [contenteditable='true'], script, style")) return;

  ATTRS.forEach((attr) => {
    const current = el.getAttribute?.(attr);
    if (!current) return;
    const originalAttr = `data-i18n-original-${attr}`;
    const original = el.getAttribute(originalAttr) || current;
    if (uiPhraseTranslations[original] && !el.hasAttribute(originalAttr)) el.setAttribute(originalAttr, original);
    if (el.hasAttribute(originalAttr)) el.setAttribute(attr, translateDynamic(el.getAttribute(originalAttr), lang));
  });

  if (el.childNodes?.length !== 1 || el.firstChild?.nodeType !== Node.TEXT_NODE) return;
  const currentText = el.textContent.trim().replace(/\s+/g, " ");
  if (!currentText) return;
  const original = el.getAttribute("data-i18n-original-text") || currentText;
  if (uiPhraseTranslations[original] && !el.hasAttribute("data-i18n-original-text")) el.setAttribute("data-i18n-original-text", original);
  if (el.hasAttribute("data-i18n-original-text")) el.textContent = translateDynamic(el.getAttribute("data-i18n-original-text"), lang);
}

function localizeTextNodes(root, lang) {
  if (typeof document === "undefined") return;
  const scope = root?.nodeType === Node.ELEMENT_NODE ? root : document.body;
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || parent.closest?.("[data-no-auto-localize], [contenteditable='true'], script, style, textarea, input")) return NodeFilter.FILTER_REJECT;
      const text = node.textContent.trim().replace(/\s+/g, " ");
      return uiPhraseTranslations[text] ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    },
  });

  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const original = node.__marstickOriginalText || node.textContent.trim().replace(/\s+/g, " ");
    node.__marstickOriginalText = original;
    const translated = translateDynamic(original, lang);
    node.textContent = node.textContent.replace(node.textContent.trim(), translated);
  });
}

function localizeTree(root, lang) {
  if (typeof document === "undefined") return;
  const scope = root?.querySelectorAll ? root : document.body;
  scope.querySelectorAll?.(TEXT_SELECTOR).forEach((el) => localizeElement(el, lang));
  localizeTextNodes(scope, lang);
}

export default function HardcodedTextLocalizer() {
  const { language } = useLanguage();

  React.useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return undefined;
    const run = () => localizeTree(document.body, language);
    run();
    const frame = requestAnimationFrame(run);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) localizeTree(node, language);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [language]);

  return null;
}